import * as React from "react"
import {
  Search,
  X,
  Calendar as CalendarIcon,
  Tag,
  Landmark,
  RotateCcw,
  Filter,
  Check,
  ChevronRight,
  ChevronLeft,
  CalendarDays,
} from "lucide-react"
import { format } from "date-fns"
import type { DateRange } from "react-day-picker"

import { Input } from "@/shared/ui/atoms/Input"
import { Button } from "@/shared/ui/atoms/Button"
import { Badge } from "@/shared/ui/atoms/Badge"
import { Combobox, type ComboboxOption } from "@/shared/ui/atoms/Combobox"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/ui/atoms/Popover"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
  DrawerFooter,
} from "@/shared/ui/atoms/Drawer"
import { Calendar } from "@/shared/ui/atoms/Calendar"
import { formatCurrency } from "@/lib/utils/formatCurrency"
import { useIsMobile } from "@/shared/hooks/useMediaQuery"
import { cn } from "@/lib/utils"

export interface MonthOption {
  value: string // e.g. "2026-08" or "ALL"
  label: string // e.g. "Agosto 2026" or "Todos los meses"
}

export interface CategoryOption {
  id: number
  name: string
}

export interface AccountOption {
  id: number
  name: string
}

export interface TableFiltersProps {
  // Search
  searchTerm: string
  onSearchChange: (val: string) => void

  // Month / Year selector
  months: MonthOption[]
  selectedMonth?: string
  onMonthChange?: (month: string) => void
  selectedMonths?: string[]
  onMonthsChange?: (months: string[]) => void

  // Date Range Picker (Optional custom range)
  dateRange?: DateRange | undefined
  onDateRangeChange?: (range: DateRange | undefined) => void

  // Category
  categories?: CategoryOption[]
  selectedCategory?: string
  onCategoryChange?: (categoryId: string) => void
  selectedCategories?: string[]
  onCategoriesChange?: (categoryIds: string[]) => void

  // Account
  accounts?: AccountOption[]
  selectedAccount?: string
  onAccountChange?: (accountId: string) => void
  selectedAccounts?: string[]
  onAccountsChange?: (accountIds: string[]) => void

  // Reset & Status
  onResetFilters: () => void
  hasActiveFilters: boolean
  totalCount: number
  filteredCount: number
  totalAmount?: number
  currency?: "HNL" | "USD"
  amountsVisible?: boolean
}

type MobileFilterView = "MENU" | "MONTHS" | "CATEGORIES" | "ACCOUNTS" | "DATE_RANGE"

export function TableFilters({
  searchTerm,
  onSearchChange,
  months,
  selectedMonth,
  onMonthChange,
  selectedMonths,
  onMonthsChange,
  dateRange,
  onDateRangeChange,
  categories,
  selectedCategory,
  onCategoryChange,
  selectedCategories,
  onCategoriesChange,
  accounts,
  selectedAccount,
  onAccountChange,
  selectedAccounts,
  onAccountsChange,
  onResetFilters,
  hasActiveFilters,
  totalCount,
  filteredCount,
  totalAmount,
  currency = "HNL",
  amountsVisible = true,
}: TableFiltersProps) {
  const isMobile = useIsMobile(768)
  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false)
  const [currentView, setCurrentView] = React.useState<MobileFilterView>("MENU")
  const [categorySearchQuery, setCategorySearchQuery] = React.useState("")

  // Reset navigation view whenever the drawer is opened or closed
  React.useEffect(() => {
    if (!mobileDrawerOpen) {
      setCurrentView("MENU")
      setCategorySearchQuery("")
    }
  }, [mobileDrawerOpen])

  const isDateRangeActive = Boolean(dateRange?.from || dateRange?.to)

  // Map months to ComboboxOptions
  const monthComboboxOptions = React.useMemo<ComboboxOption[]>(() => {
    return months.map((m) => ({
      value: m.value,
      label: m.label,
    }))
  }, [months])

  // Map categories to ComboboxOptions
  const categoryComboboxOptions = React.useMemo<ComboboxOption[]>(() => {
    if (!categories) return []
    return [
      { value: "ALL", label: "Todas las categorías" },
      ...categories.map((c) => ({
        value: String(c.id),
        label: c.name,
      })),
    ]
  }, [categories])

  // Map accounts to ComboboxOptions
  const accountComboboxOptions = React.useMemo<ComboboxOption[]>(() => {
    if (!accounts) return []
    return [
      { value: "ALL", label: "Todas las cuentas" },
      ...accounts.map((a) => ({
        value: String(a.id),
        label: a.name,
      })),
    ]
  }, [accounts])

  // Normalize multi/single categories
  const currentCategoryValues = React.useMemo(() => {
    if (selectedCategories) return selectedCategories
    if (selectedCategory) return [selectedCategory]
    return ["ALL"]
  }, [selectedCategories, selectedCategory])

  const handleCategoryValuesChange = React.useCallback(
    (vals: string[]) => {
      if (onCategoriesChange) {
        onCategoriesChange(vals)
      } else if (onCategoryChange) {
        onCategoryChange(vals.length === 1 ? vals[0] : (vals.includes("ALL") ? "ALL" : vals[0] || "ALL"))
      }
    },
    [onCategoriesChange, onCategoryChange]
  )

  const handleToggleCategory = (catIdStr: string) => {
    if (catIdStr === "ALL") {
      handleCategoryValuesChange(["ALL"])
      return
    }

    const isAll = currentCategoryValues.includes("ALL") || currentCategoryValues.length === 0
    if (isAll) {
      handleCategoryValuesChange([catIdStr])
      return
    }

    if (currentCategoryValues.includes(catIdStr)) {
      const filtered = currentCategoryValues.filter((v) => v !== catIdStr && v !== "ALL")
      handleCategoryValuesChange(filtered.length === 0 ? ["ALL"] : filtered)
    } else {
      const updated = [...currentCategoryValues.filter((v) => v !== "ALL"), catIdStr]
      const totalNonAll = categories?.length || 0
      handleCategoryValuesChange(updated.length === totalNonAll ? ["ALL"] : updated)
    }
  }

  // Filter categories by search in mobile drawer
  const filteredCategoriesForDrawer = React.useMemo(() => {
    if (!categories) return []
    if (!categorySearchQuery.trim()) return categories
    const q = categorySearchQuery.toLowerCase().trim()
    return categories.filter((c) => c.name.toLowerCase().includes(q))
  }, [categories, categorySearchQuery])

  // Normalize multi/single accounts
  const currentAccountValues = React.useMemo(() => {
    if (selectedAccounts) return selectedAccounts
    if (selectedAccount) return [selectedAccount]
    return ["ALL"]
  }, [selectedAccounts, selectedAccount])

  const handleAccountValuesChange = React.useCallback(
    (vals: string[]) => {
      if (onAccountsChange) {
        onAccountsChange(vals)
      } else if (onAccountChange) {
        onAccountChange(vals.length === 1 ? vals[0] : (vals.includes("ALL") ? "ALL" : vals[0] || "ALL"))
      }
    },
    [onAccountsChange, onAccountChange]
  )

  const handleToggleAccount = (accIdStr: string) => {
    if (accIdStr === "ALL") {
      handleAccountValuesChange(["ALL"])
      return
    }

    const isAll = currentAccountValues.includes("ALL") || currentAccountValues.length === 0
    if (isAll) {
      handleAccountValuesChange([accIdStr])
      return
    }

    if (currentAccountValues.includes(accIdStr)) {
      const filtered = currentAccountValues.filter((v) => v !== accIdStr && v !== "ALL")
      handleAccountValuesChange(filtered.length === 0 ? ["ALL"] : filtered)
    } else {
      const updated = [...currentAccountValues.filter((v) => v !== "ALL"), accIdStr]
      const totalNonAll = accounts?.length || 0
      handleAccountValuesChange(updated.length === totalNonAll ? ["ALL"] : updated)
    }
  }

  // Count active filters (excluding search term which is inline)
  const activeOverlayFiltersCount = React.useMemo(() => {
    let count = 0
    if (selectedMonth && selectedMonth !== "ALL") count++
    if (selectedMonths && selectedMonths.length > 0 && !selectedMonths.includes("ALL")) count++
    if (isDateRangeActive) count++
    if (currentCategoryValues.length > 0 && !currentCategoryValues.includes("ALL")) count++
    if (currentAccountValues.length > 0 && !currentAccountValues.includes("ALL")) count++
    return count
  }, [selectedMonth, selectedMonths, isDateRangeActive, currentCategoryValues, currentAccountValues])

  // Selected Month display label
  const selectedMonthLabel = React.useMemo(() => {
    if (isDateRangeActive) return "Rango activo"
    if (!selectedMonth || selectedMonth === "ALL") return "Todos los meses"
    const match = months.find((m) => m.value === selectedMonth)
    return match?.label || selectedMonth
  }, [selectedMonth, months, isDateRangeActive])

  // Selected Categories display label
  const isAllCategoriesSelected = currentCategoryValues.includes("ALL") || currentCategoryValues.length === 0
  const categoriesDisplayLabel = React.useMemo(() => {
    if (isAllCategoriesSelected) return "Todas"
    if (currentCategoryValues.length === 1 && categories) {
      const cat = categories.find((c) => String(c.id) === currentCategoryValues[0])
      return cat?.name || "1 seleccionada"
    }
    return `${currentCategoryValues.length} seleccionadas`
  }, [isAllCategoriesSelected, currentCategoryValues, categories])

  // Selected Accounts display label
  const isAllAccountsSelected = currentAccountValues.includes("ALL") || currentAccountValues.length === 0
  const accountsDisplayLabel = React.useMemo(() => {
    if (isAllAccountsSelected) return "Todas"
    if (currentAccountValues.length === 1 && accounts) {
      const acc = accounts.find((a) => String(a.id) === currentAccountValues[0])
      return acc?.name || "1 seleccionada"
    }
    return `${currentAccountValues.length} seleccionadas`
  }, [isAllAccountsSelected, currentAccountValues, accounts])

  // Date range display label
  const dateRangeDisplayLabel = React.useMemo(() => {
    if (!dateRange?.from) return "Sin rango"
    if (dateRange.to) {
      return `${format(dateRange.from, "dd MMM")} - ${format(dateRange.to, "dd MMM")}`
    }
    return `Desde ${format(dateRange.from, "dd MMM")}`
  }, [dateRange])

  // Common Search Input component
  const SearchInputControl = (
    <div className="relative flex-1 min-w-[180px] sm:max-w-xs">
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
      <Input
        type="text"
        placeholder="Buscar por concepto..."
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        className="h-8.5 sm:h-8 pl-8 pr-7 text-xs bg-background"
      />
      {searchTerm && (
        <button
          type="button"
          onClick={() => onSearchChange("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1 cursor-pointer"
          aria-label="Limpiar búsqueda"
        >
          <X className="size-3.5" />
        </button>
      )}
    </div>
  )

  // Status Info Row
  const StatusInfoRow = (
    <div className="flex items-center justify-between text-[11px] text-muted-foreground pt-0.5">
      <div className="flex items-center gap-1.5">
        <span>
          Mostrando <strong className="text-foreground font-semibold">{filteredCount}</strong> de {totalCount} registros
        </span>
        {hasActiveFilters && (
          <Badge variant="outline" className="text-[10px] py-0 px-1.5 font-medium text-primary border-primary/30 bg-primary/5">
            Filtrado
          </Badge>
        )}
      </div>

      {totalAmount !== undefined && (
        <div className="font-mono text-right">
          <span>Total: </span>
          <strong className="text-foreground font-semibold">
            {amountsVisible ? formatCurrency(totalAmount, currency) : "••••••"}
          </strong>
        </div>
      )}
    </div>
  )

  // =========================================================================
  // MOBILE ADAPTIVE VIEW: DRILL-DOWN NAVIGATION BOTTOM SHEET (Apple/Fintech)
  // =========================================================================
  if (isMobile) {
    return (
      <div className="flex flex-col gap-2.5 px-3 py-2.5 bg-muted/20 border-b border-border/60">
        {/* Mobile Toolbar Row */}
        <div className="flex items-center gap-2">
          {SearchInputControl}

          {/* Drawer Trigger Button */}
          <Button
            type="button"
            variant={activeOverlayFiltersCount > 0 ? "default" : "outline"}
            size="sm"
            onClick={() => setMobileDrawerOpen(true)}
            aria-label="Abrir filtros"
            className={cn(
              "h-8.5 px-3 text-xs gap-1.5 rounded-lg shrink-0 font-medium transition-all cursor-pointer",
              activeOverlayFiltersCount > 0
                ? "bg-primary text-primary-foreground shadow-xs"
                : "bg-background border-border/80 hover:bg-accent"
            )}
          >
            <Filter className="size-3.5" />
            <span>Filtros</span>
            {activeOverlayFiltersCount > 0 && (
              <span className="ml-0.5 rounded-full bg-primary-foreground text-primary px-1.5 py-0.2 text-[10px] font-bold leading-tight">
                {activeOverlayFiltersCount}
              </span>
            )}
          </Button>

          {/* Quick Reset Button if any filter or search is active */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onResetFilters}
              className="size-8.5 shrink-0 text-muted-foreground hover:text-foreground cursor-pointer"
              aria-label="Restablecer filtros"
              title="Restablecer filtros"
            >
              <RotateCcw className="size-3.5" />
            </Button>
          )}
        </div>

        {StatusInfoRow}

        {/* Drill-Down Navigation Drawer */}
        <Drawer open={mobileDrawerOpen} onOpenChange={setMobileDrawerOpen}>
          <DrawerContent className="max-h-[90dvh] flex flex-col focus:outline-none bg-card border-t border-border shadow-2xl">
            
            {/* ─────────────────────────────────────────────────────────────
                VIEW: MAIN MENU (Top Level Navigation List)
               ───────────────────────────────────────────────────────────── */}
            {currentView === "MENU" && (
              <>
                <DrawerHeader className="px-4 py-3.5 border-b border-border/60 flex flex-row items-center justify-between">
                  <div className="flex items-center gap-2">
                    <DrawerTitle className="text-base font-semibold">
                      Filtros
                    </DrawerTitle>
                    {activeOverlayFiltersCount > 0 && (
                      <Badge variant="secondary" className="text-xs px-2 py-0.5 font-semibold bg-primary/15 text-primary border-primary/20">
                        {activeOverlayFiltersCount} activo{activeOverlayFiltersCount > 1 ? "s" : ""}
                      </Badge>
                    )}
                  </div>

                  {hasActiveFilters && (
                    <button
                      type="button"
                      onClick={() => {
                        onResetFilters()
                        setCategorySearchQuery("")
                      }}
                      className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1.5 py-1 px-2.5 rounded-lg hover:bg-muted/80 transition-colors font-medium cursor-pointer"
                    >
                      <RotateCcw className="size-3" />
                      <span>Limpiar todo</span>
                    </button>
                  )}
                </DrawerHeader>

                {/* Drill-Down Menu List */}
                <div className="p-4 flex-1 overflow-y-auto">
                  <div className="rounded-2xl border border-border/60 bg-muted/15 divide-y divide-border/60 overflow-hidden shadow-2xs">
                    
                    {/* Row 1: Periodo / Mes */}
                    {months.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setCurrentView("MONTHS")}
                        className="w-full h-14 px-4 flex items-center justify-between text-left hover:bg-muted/30 active:bg-muted/50 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                            <CalendarIcon className="size-4" />
                          </div>
                          <div className="flex flex-col">
                            <span className="text-xs font-semibold text-foreground">
                              Periodo / Mes
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              Selecciona el mes a consultar
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5">
                          <span className={cn(
                            "text-xs font-medium max-w-[130px] truncate",
                            selectedMonth && selectedMonth !== "ALL" ? "text-primary font-semibold" : "text-muted-foreground"
                          )}>
                            {selectedMonthLabel}
                          </span>
                          <ChevronRight className="size-4 text-muted-foreground shrink-0" />
                        </div>
                      </button>
                    )}

                    {/* Row 2: Rango Personalizado */}
                    {onDateRangeChange && (
                      <button
                        type="button"
                        onClick={() => setCurrentView("DATE_RANGE")}
                        className="w-full h-14 px-4 flex items-center justify-between text-left hover:bg-muted/30 active:bg-muted/50 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                            <CalendarDays className="size-4" />
                          </div>
                          <div className="flex flex-col">
                            <span className="text-xs font-semibold text-foreground">
                              Rango de fechas
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              Intervalo exacto de días
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5">
                          <span className={cn(
                            "text-xs font-medium max-w-[130px] truncate",
                            isDateRangeActive ? "text-amber-500 font-semibold" : "text-muted-foreground"
                          )}>
                            {dateRangeDisplayLabel}
                          </span>
                          <ChevronRight className="size-4 text-muted-foreground shrink-0" />
                        </div>
                      </button>
                    )}

                    {/* Row 3: Categorías */}
                    {categories && categories.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setCurrentView("CATEGORIES")}
                        className="w-full h-14 px-4 flex items-center justify-between text-left hover:bg-muted/30 active:bg-muted/50 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                            <Tag className="size-4" />
                          </div>
                          <div className="flex flex-col">
                            <span className="text-xs font-semibold text-foreground">
                              Categorías
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              {categories.length} categorías disponibles
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5">
                          <span className={cn(
                            "text-xs font-medium max-w-[130px] truncate",
                            !isAllCategoriesSelected ? "text-primary font-semibold" : "text-muted-foreground"
                          )}>
                            {categoriesDisplayLabel}
                          </span>
                          <ChevronRight className="size-4 text-muted-foreground shrink-0" />
                        </div>
                      </button>
                    )}

                    {/* Row 4: Cuentas */}
                    {accounts && accounts.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setCurrentView("ACCOUNTS")}
                        className="w-full h-14 px-4 flex items-center justify-between text-left hover:bg-muted/30 active:bg-muted/50 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                            <Landmark className="size-4" />
                          </div>
                          <div className="flex flex-col">
                            <span className="text-xs font-semibold text-foreground">
                              Cuentas
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              {accounts.length} cuentas configuradas
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5">
                          <span className={cn(
                            "text-xs font-medium max-w-[130px] truncate",
                            !isAllAccountsSelected ? "text-primary font-semibold" : "text-muted-foreground"
                          )}>
                            {accountsDisplayLabel}
                          </span>
                          <ChevronRight className="size-4 text-muted-foreground shrink-0" />
                        </div>
                      </button>
                    )}

                  </div>
                </div>

                {/* Footer Button */}
                <DrawerFooter className="p-4 border-t border-border/60 bg-card">
                  <DrawerClose asChild>
                    <Button className="w-full h-12 text-sm font-semibold rounded-xl gap-2 shadow-md cursor-pointer">
                      <span>{`Ver ${filteredCount} resultado${filteredCount === 1 ? "" : "s"}`}</span>
                    </Button>
                  </DrawerClose>
                </DrawerFooter>
              </>
            )}

            {/* ─────────────────────────────────────────────────────────────
                VIEW: MONTHS SELECTION (Drill-Down)
               ───────────────────────────────────────────────────────────── */}
            {currentView === "MONTHS" && (
              <>
                <DrawerHeader className="px-4 py-3 border-b border-border/60 flex flex-row items-center justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentView("MENU")}
                    className="h-8 px-2 text-xs font-medium gap-1 text-muted-foreground hover:text-foreground cursor-pointer -ml-2"
                  >
                    <ChevronLeft className="size-4" />
                    <span>Volver</span>
                  </Button>
                  <DrawerTitle className="text-sm font-semibold">
                    Seleccionar periodo
                  </DrawerTitle>
                  <div className="w-12" />
                </DrawerHeader>

                <div className="flex-1 overflow-y-auto divide-y divide-border/40 p-2">
                  {months.map((m) => {
                    const isSelected = !isDateRangeActive && (selectedMonth === m.value || (!selectedMonth && m.value === "ALL"))
                    return (
                      <button
                        key={m.value}
                        type="button"
                        onClick={() => {
                          if (onDateRangeChange && isDateRangeActive) {
                            onDateRangeChange(undefined)
                          }
                          onMonthChange?.(m.value)
                          setCurrentView("MENU")
                        }}
                        className="w-full h-12 px-4 flex items-center justify-between rounded-xl hover:bg-muted/50 active:bg-muted transition-colors cursor-pointer text-left"
                      >
                        <span className={cn(
                          "text-sm",
                          isSelected ? "font-semibold text-primary" : "text-foreground font-normal"
                        )}>
                          {m.label}
                        </span>
                        {isSelected && (
                          <Check className="size-4 text-primary stroke-[2.5]" />
                        )}
                      </button>
                    )
                  })}
                </div>
              </>
            )}

            {/* ─────────────────────────────────────────────────────────────
                VIEW: DATE RANGE SELECTION (Drill-Down)
               ───────────────────────────────────────────────────────────── */}
            {currentView === "DATE_RANGE" && onDateRangeChange && (
              <>
                <DrawerHeader className="px-4 py-3 border-b border-border/60 flex flex-row items-center justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentView("MENU")}
                    className="h-8 px-2 text-xs font-medium gap-1 text-muted-foreground hover:text-foreground cursor-pointer -ml-2"
                  >
                    <ChevronLeft className="size-4" />
                    <span>Volver</span>
                  </Button>
                  <DrawerTitle className="text-sm font-semibold">
                    Rango personalizado
                  </DrawerTitle>
                  {isDateRangeActive ? (
                    <button
                      type="button"
                      onClick={() => onDateRangeChange(undefined)}
                      className="text-xs text-destructive hover:underline font-medium cursor-pointer"
                    >
                      Quitar
                    </button>
                  ) : (
                    <div className="w-10" />
                  )}
                </DrawerHeader>

                <div className="flex-1 overflow-y-auto p-4 flex flex-col items-center gap-3">
                  <div className="rounded-2xl border border-border/80 p-3 bg-background shadow-xs w-full flex flex-col items-center">
                    <Calendar
                      mode="range"
                      defaultMonth={dateRange?.from || new Date()}
                      selected={dateRange}
                      onSelect={onDateRangeChange}
                      numberOfMonths={1}
                      className="p-1"
                    />
                  </div>

                  {dateRange?.from && (
                    <div className="text-xs text-muted-foreground text-center font-medium bg-muted/30 py-2 px-3 rounded-xl border border-border/50 w-full">
                      {dateRange.to ? (
                        <span>
                          Rango seleccionado: <strong className="text-foreground font-semibold">{format(dateRange.from, "dd MMM yyyy")}</strong> — <strong className="text-foreground font-semibold">{format(dateRange.to, "dd MMM yyyy")}</strong>
                        </span>
                      ) : (
                        <span>
                          Inicio: <strong className="text-foreground font-semibold">{format(dateRange.from, "dd MMM yyyy")}</strong> (toca otra fecha para fin)
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <DrawerFooter className="p-4 border-t border-border/60 bg-card">
                  <Button
                    onClick={() => setCurrentView("MENU")}
                    className="w-full h-11 text-sm font-semibold rounded-xl cursor-pointer"
                  >
                    Listo
                  </Button>
                </DrawerFooter>
              </>
            )}

            {/* ─────────────────────────────────────────────────────────────
                VIEW: CATEGORIES SELECTION (Drill-Down with Sticky Search)
               ───────────────────────────────────────────────────────────── */}
            {currentView === "CATEGORIES" && categories && (
              <>
                <DrawerHeader className="px-4 py-3 border-b border-border/60 flex flex-row items-center justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentView("MENU")}
                    className="h-8 px-2 text-xs font-medium gap-1 text-muted-foreground hover:text-foreground cursor-pointer -ml-2"
                  >
                    <ChevronLeft className="size-4" />
                    <span>Volver</span>
                  </Button>
                  <DrawerTitle className="text-sm font-semibold">
                    Categorías ({isAllCategoriesSelected ? "Todas" : currentCategoryValues.length})
                  </DrawerTitle>
                  <button
                    type="button"
                    onClick={() => handleCategoryValuesChange(["ALL"])}
                    className="text-xs text-primary hover:underline font-medium cursor-pointer"
                  >
                    {isAllCategoriesSelected ? "Todas" : "Restablecer"}
                  </button>
                </DrawerHeader>

                {/* Sticky Search Box */}
                <div className="px-4 py-2.5 bg-card border-b border-border/50">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
                    <Input
                      type="text"
                      placeholder="Buscar categoría..."
                      value={categorySearchQuery}
                      onChange={(e) => setCategorySearchQuery(e.target.value)}
                      className="h-9.5 text-xs pl-9 pr-8 bg-muted/30 rounded-xl border-border/70"
                    />
                    {categorySearchQuery && (
                      <button
                        type="button"
                        onClick={() => setCategorySearchQuery("")}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1 cursor-pointer"
                      >
                        <X className="size-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Vertical Category Rows */}
                <div className="flex-1 overflow-y-auto divide-y divide-border/40 p-2">
                  {/* Row: Todas las categorías */}
                  {!categorySearchQuery && (
                    <button
                      type="button"
                      onClick={() => handleCategoryValuesChange(["ALL"])}
                      className="w-full h-12 px-4 flex items-center justify-between rounded-xl hover:bg-muted/50 active:bg-muted transition-colors cursor-pointer text-left"
                    >
                      <span className={cn(
                        "text-sm font-medium",
                        isAllCategoriesSelected ? "text-primary font-semibold" : "text-foreground"
                      )}>
                        Todas las categorías
                      </span>
                      {isAllCategoriesSelected && (
                        <Check className="size-4 text-primary stroke-[2.5]" />
                      )}
                    </button>
                  )}

                  {/* Individual Categories */}
                  {filteredCategoriesForDrawer.length === 0 ? (
                    <div className="py-8 text-center text-xs text-muted-foreground">
                      No se encontraron categorías para &ldquo;{categorySearchQuery}&rdquo;
                    </div>
                  ) : (
                    filteredCategoriesForDrawer.map((cat) => {
                      const isSelected = !isAllCategoriesSelected && currentCategoryValues.includes(String(cat.id))
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => handleToggleCategory(String(cat.id))}
                          className="w-full h-12 px-4 flex items-center justify-between rounded-xl hover:bg-muted/50 active:bg-muted transition-colors cursor-pointer text-left"
                        >
                          <span className={cn(
                            "text-sm",
                            isSelected ? "font-semibold text-primary" : "text-foreground font-normal"
                          )}>
                            {cat.name}
                          </span>
                          {isSelected && (
                            <Check className="size-4 text-primary stroke-[2.5]" />
                          )}
                        </button>
                      )
                    })
                  )}
                </div>

                <DrawerFooter className="p-4 border-t border-border/60 bg-card">
                  <Button
                    onClick={() => setCurrentView("MENU")}
                    className="w-full h-11 text-sm font-semibold rounded-xl cursor-pointer"
                  >
                    Listo
                  </Button>
                </DrawerFooter>
              </>
            )}

            {/* ─────────────────────────────────────────────────────────────
                VIEW: ACCOUNTS SELECTION (Drill-Down)
               ───────────────────────────────────────────────────────────── */}
            {currentView === "ACCOUNTS" && accounts && (
              <>
                <DrawerHeader className="px-4 py-3 border-b border-border/60 flex flex-row items-center justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentView("MENU")}
                    className="h-8 px-2 text-xs font-medium gap-1 text-muted-foreground hover:text-foreground cursor-pointer -ml-2"
                  >
                    <ChevronLeft className="size-4" />
                    <span>Volver</span>
                  </Button>
                  <DrawerTitle className="text-sm font-semibold">
                    Cuentas ({isAllAccountsSelected ? "Todas" : currentAccountValues.length})
                  </DrawerTitle>
                  <button
                    type="button"
                    onClick={() => handleAccountValuesChange(["ALL"])}
                    className="text-xs text-primary hover:underline font-medium cursor-pointer"
                  >
                    {isAllAccountsSelected ? "Todas" : "Restablecer"}
                  </button>
                </DrawerHeader>

                <div className="flex-1 overflow-y-auto divide-y divide-border/40 p-2">
                  {/* Row: Todas las cuentas */}
                  <button
                    type="button"
                    onClick={() => handleAccountValuesChange(["ALL"])}
                    className="w-full h-12 px-4 flex items-center justify-between rounded-xl hover:bg-muted/50 active:bg-muted transition-colors cursor-pointer text-left"
                  >
                    <div className="flex items-center gap-2.5">
                      <Landmark className="size-4 text-muted-foreground" />
                      <span className={cn(
                        "text-sm font-medium",
                        isAllAccountsSelected ? "text-primary font-semibold" : "text-foreground"
                      )}>
                        Todas las cuentas
                      </span>
                    </div>
                    {isAllAccountsSelected && (
                      <Check className="size-4 text-primary stroke-[2.5]" />
                    )}
                  </button>

                  {/* Individual Accounts */}
                  {accounts.map((acc) => {
                    const isSelected = !isAllAccountsSelected && currentAccountValues.includes(String(acc.id))
                    return (
                      <button
                        key={acc.id}
                        type="button"
                        onClick={() => handleToggleAccount(String(acc.id))}
                        className="w-full h-12 px-4 flex items-center justify-between rounded-xl hover:bg-muted/50 active:bg-muted transition-colors cursor-pointer text-left"
                      >
                        <div className="flex items-center gap-2.5">
                          <Landmark className="size-4 text-muted-foreground" />
                          <span className={cn(
                            "text-sm",
                            isSelected ? "font-semibold text-primary" : "text-foreground font-normal"
                          )}>
                            {acc.name}
                          </span>
                        </div>
                        {isSelected && (
                          <Check className="size-4 text-primary stroke-[2.5]" />
                        )}
                      </button>
                    )
                  })}
                </div>

                <DrawerFooter className="p-4 border-t border-border/60 bg-card">
                  <Button
                    onClick={() => setCurrentView("MENU")}
                    className="w-full h-11 text-sm font-semibold rounded-xl cursor-pointer"
                  >
                    Listo
                  </Button>
                </DrawerFooter>
              </>
            )}

          </DrawerContent>
        </Drawer>
      </div>
    )
  }

  // ==========================================
  // DESKTOP VIEW (Compact Inline Toolbar)
  // ==========================================
  return (
    <div className="flex flex-col gap-2.5 px-4 py-3 bg-muted/20 border-b border-border/60">
      {/* Controls Row */}
      <div className="flex flex-wrap items-center gap-2">
        {SearchInputControl}

        {/* Month Selector Combobox */}
        {months.length > 0 && !isDateRangeActive && (
          <div className="w-auto">
            {onMonthsChange ? (
              <Combobox
                multiple={true}
                values={selectedMonths || (selectedMonth ? [selectedMonth] : ["ALL"])}
                onValuesChange={onMonthsChange}
                options={monthComboboxOptions}
                icon={CalendarIcon}
                placeholder="Mes"
                allLabel="Todos los meses"
                searchPlaceholder="Buscar mes o año..."
                emptyText="No se encontraron meses."
                itemNounSingular="mes"
                itemNounPlural="meses"
              />
            ) : (
              <Combobox
                multiple={false}
                value={selectedMonth || "ALL"}
                onValueChange={(val) => onMonthChange?.(val)}
                options={monthComboboxOptions}
                icon={CalendarIcon}
                placeholder="Mes"
                allLabel="Todos los meses"
                searchPlaceholder="Buscar mes o año..."
                emptyText="No se encontraron meses."
              />
            )}
          </div>
        )}

        {/* Date Range Picker (Shadcn Calendar Popover) */}
        {onDateRangeChange && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={isDateRangeActive ? "secondary" : "outline"}
                size="sm"
                className="h-8 text-xs font-normal bg-background gap-1.5 px-2.5 cursor-pointer"
              >
                <CalendarIcon className="size-3 text-muted-foreground shrink-0" />
                {dateRange?.from ? (
                  dateRange.to ? (
                    <span>
                      {format(dateRange.from, "dd MMM")} - {format(dateRange.to, "dd MMM, yyyy")}
                    </span>
                  ) : (
                    <span>Desde {format(dateRange.from, "dd MMM, yyyy")}</span>
                  )
                ) : (
                  <span>Rango de fechas</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="range"
                defaultMonth={dateRange?.from || new Date()}
                selected={dateRange}
                onSelect={onDateRangeChange}
                numberOfMonths={2}
              />
              {isDateRangeActive && (
                <div className="p-2 border-t border-border flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs px-2 cursor-pointer"
                    onClick={() => onDateRangeChange(undefined)}
                  >
                    Quitar rango
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        )}

        {/* Category Multi-Select Combobox */}
        {categories && categories.length > 0 && (onCategoryChange || onCategoriesChange) && (
          <div className="w-auto">
            <Combobox
              multiple={true}
              values={currentCategoryValues}
              onValuesChange={handleCategoryValuesChange}
              options={categoryComboboxOptions}
              icon={Tag}
              placeholder="Categoría"
              allLabel="Todas las categorías"
              searchPlaceholder="Buscar categoría..."
              emptyText="No se encontraron categorías."
              itemNounSingular="categoría"
              itemNounPlural="categorías"
            />
          </div>
        )}

        {/* Account Multi-Select Combobox */}
        {accounts && accounts.length > 0 && (onAccountChange || onAccountsChange) && (
          <div className="w-auto">
            <Combobox
              multiple={true}
              values={currentAccountValues}
              onValuesChange={handleAccountValuesChange}
              options={accountComboboxOptions}
              icon={Landmark}
              placeholder="Cuenta"
              allLabel="Todas las cuentas"
              searchPlaceholder="Buscar cuenta..."
              emptyText="No se encontraron cuentas."
              itemNounSingular="cuenta"
              itemNounPlural="cuentas"
            />
          </div>
        )}

        {/* Reset Filters Button */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onResetFilters}
            className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground gap-1 ml-auto sm:ml-0 cursor-pointer"
          >
            <RotateCcw className="size-3" />
            <span>Restablecer</span>
          </Button>
        )}
      </div>

      {StatusInfoRow}
    </div>
  )
}

export default TableFilters
